import React, { useState, useEffect } from "react";
import { CommonInput } from "@/components/ui/common-input";
import { CommonSelect } from "@/components/ui/common-select";
import { CommonButton } from "@/components/ui/common-button";
import { DateTimeInput } from "@/components/ui/DateTimeInput";
import { Plus, X } from "lucide-react";
import masterDataService from "@/services/masterDataService";
import productService from "@/services/productService";
import rakeMovementService from "@/services/rakeMovementService";
import Loader from "@/components/ui/loader";

const CreateRakeMovementForm = ({ onCreateRakeMovement, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    port_id: "",
    scheduled_start_time: "",
    scheduled_end_time: "",
    state_id: "",
    district_id: "",
    rake_point_id: "",
    buffer_godown_rake_point_id: "",
    rr_number: "",
    rr_date: "",
    baseline_qty_mt: ""
  });

  const [items, setItems] = useState([
    { id: Date.now(), product_type_id: "", quantity_mt: "" }
  ]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // Master Data state
  const [ports, setPorts] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [rakePoints, setRakePoints] = useState([]);
  const [godowns, setGodowns] = useState([]);

  // Load Ports, States, ProductTypes on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [portsRes, statesRes, productsRes] = await Promise.all([
          masterDataService.getPorts(0, 100).catch(() => []),
          masterDataService.getStates(true).catch(() => []),
          productService.getProducts(0, 500).catch(() => [])
        ]);

        setPorts(Array.isArray(portsRes) ? portsRes : portsRes?.data || []);
        setStates(Array.isArray(statesRes) ? statesRes : statesRes?.data || []);
        setProductTypes(Array.isArray(productsRes) ? productsRes : productsRes?.data || []);
      } catch (err) {
        console.error("Failed to load initial master data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  // Options Mapping
  const portOptions = ports.map(p => ({
    value: p.id,
    label: `${p.port_name || p.name || p.id}${p.port_code ? ` (${p.port_code})` : ''}`
  }));

  const stateOptions = states.map(s => ({
    value: s.id,
    label: s.name || s.state_name || s.id
  }));

  const districtOptions = districts.map(d => ({
    value: d.id,
    label: d.name || d.district_name || d.id
  }));

  const productTypeOptions = productTypes.map(pt => ({
    value: pt.id,
    label: `${pt.name || pt.id}${pt.unit_of_measure ? ` (${pt.unit_of_measure})` : ''}`
  }));

  const rakePointOptions = rakePoints.map(rp => ({
    value: rp.id,
    label: `${rp.name || rp.code || rp.id}${rp.code ? ` (${rp.code})` : ''}`
  }));

  const godownOptions = godowns.map(g => ({
    value: g.id,
    label: `${g.name || g.code || g.id}${g.code ? ` (${g.code})` : ''}`
  }));

  const handleChange = (field, val) => {
    const rawVal = (val && typeof val === 'object' && val?.target?.value !== undefined) ? val.target.value : val;
    setFormData(prev => ({ ...prev, [field]: rawVal }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  // State Change -> Fetch Districts
  const handleStateChange = async (stateId) => {
    handleChange("state_id", stateId);
    handleChange("district_id", "");
    handleChange("rake_point_id", "");
    handleChange("buffer_godown_rake_point_id", "");
    setDistricts([]);
    setRakePoints([]);
    setGodowns([]);

    if (!stateId) return;
    try {
      const res = await masterDataService.getDistrictsByState(stateId, true);
      setDistricts(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      console.error("Failed to load districts:", err);
    }
  };

  // District Change -> Fetch Rake Points Server Side via /api/v1/rake-movements/rake-points/dropdown
  const handleDistrictChange = async (districtId) => {
    handleChange("district_id", districtId);
    handleChange("rake_point_id", "");
    handleChange("buffer_godown_rake_point_id", "");
    setRakePoints([]);
    setGodowns([]);

    if (!districtId) return;
    try {
      const [rRes, gRes] = await Promise.all([
        rakeMovementService.getRakePointsDropdown({ state_id: formData.state_id, district_id: districtId, point_type: 'UNLOADING_POINT' }).catch(() => []),
        rakeMovementService.getRakePointsDropdown({ state_id: formData.state_id, district_id: districtId, point_type: 'GODOWN' }).catch(() => [])
      ]);

      setRakePoints(Array.isArray(rRes) ? rRes : rRes?.data || []);
      setGodowns(Array.isArray(gRes) ? gRes : gRes?.data || []);
    } catch (err) {
      console.error("Failed to load rake points from server:", err);
    }
  };

  // Product Lines Handlers
  const recalculateBaseline = (currentItems) => {
    const totalQty = currentItems.reduce((sum, item) => {
      const val = parseFloat(item.quantity_mt);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
    setFormData(prev => ({
      ...prev,
      baseline_qty_mt: totalQty > 0 ? totalQty.toFixed(2) : ""
    }));
  };

  const addItem = () => setItems(prev => [...prev, { id: Date.now(), product_type_id: "", quantity_mt: "" }]);
  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(prev => {
        const updated = prev.filter(i => i.id !== id);
        recalculateBaseline(updated);
        return updated;
      });
    }
  };
  const updateItem = (id, field, val) => {
    setItems(prev => {
      const updated = prev.map(i => i.id === id ? { ...i, [field]: val } : i);
      if (field === "quantity_mt") {
        recalculateBaseline(updated);
      }
      return updated;
    });
  };

  // Validation
  const validate = () => {
    const errs = {};
    if (!formData.port_id) errs.port_id = "Required";
    if (!formData.scheduled_start_time) errs.scheduled_start_time = "Required";
    if (!formData.scheduled_end_time) errs.scheduled_end_time = "Required";
    if (!formData.state_id) errs.state_id = "Required";
    if (!formData.district_id) errs.district_id = "Required";
    if (!formData.rake_point_id) errs.rake_point_id = "Required";
    if (!formData.rr_number.trim()) errs.rr_number = "Required";
    if (!formData.rr_date) errs.rr_date = "Required";
    if (formData.baseline_qty_mt && (isNaN(parseFloat(formData.baseline_qty_mt)) || parseFloat(formData.baseline_qty_mt) <= 0)) {
      errs.baseline_qty_mt = "Must be a positive number";
    }

    const validItems = items.filter(i => i.product_type_id && parseFloat(i.quantity_mt) > 0);
    if (validItems.length === 0) errs.items = "Add at least 1 product line";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      port_id: formData.port_id,
      scheduled_start_time: new Date(formData.scheduled_start_time).toISOString(),
      scheduled_end_time: new Date(formData.scheduled_end_time).toISOString(),
      state_id: formData.state_id,
      district_id: formData.district_id,
      rake_point_id: formData.rake_point_id,
      buffer_godown_rake_point_id: formData.buffer_godown_rake_point_id || undefined,
      rr_number: formData.rr_number.trim(),
      rr_date: new Date(formData.rr_date).toISOString(),
      baseline_qty_mt: formData.baseline_qty_mt ? parseFloat(formData.baseline_qty_mt) : undefined,
      items: items
        .filter(i => i.product_type_id && parseFloat(i.quantity_mt) > 0)
        .map((i, idx) => ({
          product_type_id: i.product_type_id,
          quantity_mt: parseFloat(i.quantity_mt),
          item_sequence: idx + 1
        }))
    };

    onCreateRakeMovement(payload);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <Loader size="md" message="Loading master data..." />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Primary Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CommonSelect
          label="Port"
          id="port_id"
          value={formData.port_id}
          onValueChange={(val) => handleChange("port_id", val)}
          options={portOptions}
          error={errors.port_id}
          required
          placeholder="Select Port"
          searchable
        />

        <DateTimeInput
          label="Scheduled Start Time"
          id="scheduled_start_time"
          value={formData.scheduled_start_time}
          onChange={(val) => handleChange("scheduled_start_time", val)}
          error={errors.scheduled_start_time}
          required
          placeholder="Select start date & time"
          minDate={new Date()}
          defaultToCurrentTime={true}
        />

        <DateTimeInput
          label="Scheduled End Time"
          id="scheduled_end_time"
          value={formData.scheduled_end_time}
          onChange={(val) => handleChange("scheduled_end_time", val)}
          error={errors.scheduled_end_time}
          required
          placeholder="Select end date & time"
          minDate={new Date()}
          defaultToCurrentTime={false}
        />

        <CommonSelect
          label="State"
          id="state_id"
          value={formData.state_id}
          onValueChange={handleStateChange}
          options={stateOptions}
          error={errors.state_id}
          required
          placeholder="Select State"
          searchable
        />

        <CommonSelect
          label="District"
          id="district_id"
          value={formData.district_id}
          onValueChange={handleDistrictChange}
          options={districtOptions}
          error={errors.district_id}
          required
          placeholder={formData.state_id ? "Select District" : "Select State first"}
          searchable
          disabled={!formData.state_id}
        />

        <CommonSelect
          label="Rake Point"
          id="rake_point_id"
          value={formData.rake_point_id}
          onValueChange={(val) => handleChange("rake_point_id", val)}
          options={rakePointOptions}
          error={errors.rake_point_id}
          required
          placeholder={formData.district_id ? "Select Rake Point" : "Select District first"}
          searchable
          disabled={!formData.district_id}
        />

        <CommonSelect
          label="Buffer Godown (Optional)"
          id="buffer_godown_rake_point_id"
          value={formData.buffer_godown_rake_point_id}
          onValueChange={(val) => handleChange("buffer_godown_rake_point_id", val)}
          options={godownOptions}
          placeholder={formData.district_id ? "Select Buffer Godown" : "Select District first"}
          searchable
          disabled={!formData.district_id}
        />

        <CommonInput
          label="RR Number"
          id="rr_number"
          value={formData.rr_number}
          onChange={(e) => handleChange("rr_number", e.target.value)}
          error={errors.rr_number}
          required
          placeholder="Enter RR Number"
        />

        <DateTimeInput
          label="RR Date"
          id="rr_date"
          value={formData.rr_date}
          onChange={(val) => handleChange("rr_date", val)}
          error={errors.rr_date}
          required
          placeholder="Select RR Date"
        />

        <CommonInput
          label="Baseline Qty (MT)"
          id="baseline_qty_mt"
          type="number"
          step="0.01"
          value={formData.baseline_qty_mt}
          onChange={(e) => handleChange("baseline_qty_mt", e.target.value)}
          error={errors.baseline_qty_mt}
          placeholder="Enter Baseline Qty (MT)"
        />
      </div>

      {/* Product Lines */}
      <div className="pt-2">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-semibold text-foreground">Product Lines</label>
          <CommonButton type="button" variant="outline" size="sm" onClick={addItem} className="h-8 text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Product
          </CommonButton>
        </div>

        {errors.items && <p className="text-xs text-destructive mb-2">{errors.items}</p>}

        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-3 mb-2">
            <div className="flex-1">
              <CommonSelect
                id={`product_type_${item.id}`}
                value={item.product_type_id}
                onValueChange={(val) => updateItem(item.id, "product_type_id", val)}
                options={productTypeOptions}
                placeholder="Select Product"
                searchable
              />
            </div>
            <div className="w-36">
              <CommonInput
                id={`quantity_mt_${item.id}`}
                type="number"
                step="0.01"
                value={item.quantity_mt}
                onChange={(e) => updateItem(item.id, "quantity_mt", e.target.value)}
                placeholder="Qty (MT)"
              />
            </div>
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                className="text-red-500 hover:text-red-700 p-1 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-4 border-t">
        <CommonButton type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </CommonButton>
        <CommonButton type="submit" variant="default" loading={isSubmitting}>
          Create Rake Movement
        </CommonButton>
      </div>
    </form>
  );
};

export default CreateRakeMovementForm;
